import { useEffect, useRef, useState } from "react";
import { ArrowUpIcon, TelegramIcon } from "vega-icons";

import { useUserProfile } from "@vega-api/extended/auth/hooks";
import { ThemedButton } from "@vega-core/components/Buttons/ThemedButton";
import { ThemedIconButton } from "@vega-core/components/Buttons/ThemedIconButton";
import { TextareaInput } from "@vega-core/components/Form/Controls/Textarea/TextareaInput";
import { Kbd } from "@vega-core/components/shadcn/kbd";
import { m } from "@vega-core/paraglide/messages";
import { cnTwMerge } from "@vega-core/utils/cnTwMerge";
import {
  DateFormatString,
  formatDateByPattern,
} from "@vega-core/utils/formatDate";
import { richTextFormat } from "@vega-core/utils/i18nHelpers";

import {
  getMessageSenderName,
  useAutoMarkConversationRead,
  useInfiniteConversationMessages,
  useSendConversationMessage,
} from "./conversationsData";
import { MessagingErrorState } from "./MessagingErrorState";

type Props = {
  conversationId: string;
  testIdPrefix: string;
};

export const ConversationThread = ({ conversationId, testIdPrefix }: Props) => {
  const profileQuery = useUserProfile();
  const [replyBody, setReplyBody] = useState("");

  const messagesQuery = useInfiniteConversationMessages(conversationId);
  const sendMessageMutation = useSendConversationMessage(conversationId);

  const messages =
    messagesQuery.data?.pages.flatMap((page) => page.data).toReversed() ?? [];
  const newestMessageId = messages.at(-1)?.messageId;

  useAutoMarkConversationRead({ conversationId, newestMessageId });
  const replyBoxRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (newestMessageId) {
      replyBoxRef.current?.scrollIntoView({ block: "end" });
    }
  }, [newestMessageId]);

  const currentUserId = profileQuery.data?.id;
  const canSend = !!replyBody.trim();

  const handleSend = () => {
    if (!canSend) return;
    setReplyBody("");

    sendMessageMutation.mutate({
      conversationId,
      data: { messageContent: replyBody.trim() },
    });
  };

  if (messagesQuery.isError && messages.length === 0) {
    return (
      <MessagingErrorState
        isRetrying={messagesQuery.isRefetching}
        onRetry={() => messagesQuery.refetch()}
        testId={`${testIdPrefix}-messages-error`}
      />
    );
  }

  return (
    <>
      <div
        className="mt-6 flex flex-col gap-4"
        data-testid={`${testIdPrefix}-conversation-messages`}
      >
        {messagesQuery.hasNextPage ? (
          <ThemedButton
            className="self-center"
            dataTestId={`${testIdPrefix}-conversation-load-older`}
            icon={<ArrowUpIcon />}
            isLoading={messagesQuery.isFetchingNextPage}
            onClick={() => messagesQuery.fetchNextPage()}
            variant="text"
          >
            {m.btn_showMore()}
          </ThemedButton>
        ) : null}
        {messagesQuery.isLoading ? (
          <>
            <div className="skeleton-box h-24 w-full max-w-3xl rounded-lg" />
            <div className="skeleton-box h-24 w-full max-w-3xl self-end rounded-lg" />
            <div className="skeleton-box h-24 w-full max-w-3xl rounded-lg" />
          </>
        ) : null}
        {messages.map((message) => (
          <div
            className={cnTwMerge(
              "w-full max-w-3xl rounded-lg border border-border/primary p-4",
              message.senderId === currentUserId &&
                "self-end bg-background/secondary",
            )}
            data-testid={`${testIdPrefix}-message-card`}
            key={message.messageId}
          >
            <div className="flex items-center gap-2">
              <span className="body-base-semibold text-text/primary">
                {getMessageSenderName(message)}
              </span>
              <span className="body-small-regular text-text/secondary">
                {formatDateByPattern(
                  message.createdAt,
                  DateFormatString.US_DATE_WITH_COMMA,
                )}
              </span>
            </div>
            <p className="mt-2 body-base-regular text-text/primary whitespace-pre-wrap">
              {message.messageContent}
            </p>
          </div>
        ))}
      </div>

      <div className="mt-6 flex items-end gap-3 scroll-mb-8" ref={replyBoxRef}>
        <div className="flex-1">
          <TextareaInput
            aria-describedby={`${testIdPrefix}-message-reply-input-hint`}
            dataTestId={`${testIdPrefix}-message-reply-input`}
            fullWidth
            name="messageReply"
            onChange={(event) => setReplyBody(event.target.value)}
            onKeyDown={(event) => {
              if (
                event.key === "Enter" &&
                !event.shiftKey &&
                !event.nativeEvent.isComposing
              ) {
                event.preventDefault();
                handleSend();
              }
            }}
            placeholder={m.coInvestments_messages_replyPlaceholder()}
            rows={3}
            value={replyBody}
            withBorder
          />
        </div>
        <ThemedIconButton
          ariaLabel={m.coInvestments_messages_sendAriaLabel()}
          dataTestId={`${testIdPrefix}-message-reply-send`}
          disabled={!canSend}
          icon={<TelegramIcon />}
          onClick={handleSend}
          size="medium"
          type="button"
        />
      </div>
      <p
        className="mt-1.5 body-small-regular text-text/secondary"
        id={`${testIdPrefix}-message-reply-input-hint`}
      >
        {richTextFormat(m.coInvestments_messages_composerHint(), {
          kbd: ({ children }) => <Kbd>{children}</Kbd>,
        })}
      </p>
    </>
  );
};
